import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import aviso from '../lib/aviso';
import {
    LuChevronLeft, LuCircleAlert, LuFileText, LuFolder, LuPencil, LuPlus, LuTag, LuHistory,
} from 'react-icons/lu';
import ReactQuill from 'react-quill-new';
import PageHeader from '../components/PageHeader';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import Select from '../components/Select';
import Dialog from '../components/Dialog';
import { HelpService } from '../services/HelpService';
import type { HelpArticle, HelpCategory } from '../types/Help';
import 'react-quill-new/dist/quill.snow.css';
import './HelpArticleEditorPage.css';

/* ─── Domínio ──────────────────────────────────────────────────── */

interface Formulario {
    titulo: string;
    conteudo: string;
    categoriaId: number;
    tags: string;
}

const VAZIO: Formulario = { titulo: '', conteudo: '', categoriaId: 0, tags: '' };

/** O Quill deixa `<p><br></p>` quando a pessoa apaga tudo — isso é vazio. */
const semTexto = (html: string) =>
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;

const dataCurta = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

/* ─── Página ───────────────────────────────────────────────────── */

const HelpArticleEditorPage = () => {
    const { id } = useParams<{ id: string }>();
    const navegar = useNavigate();
    const editando = !!id;

    const [categorias, setCategorias] = useState<HelpCategory[]>([]);
    const [artigo, setArtigo] = useState<HelpArticle | null>(null);
    const [form, setForm] = useState<Formulario>(VAZIO);
    const [original, setOriginal] = useState<Formulario>(VAZIO);

    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [enviandoImagem, setEnviandoImagem] = useState(false);
    const [erros, setErros] = useState<Partial<Record<keyof Formulario, string>>>({});
    const [aDescartar, setADescartar] = useState(false);

    /* Categoria nova sem sair da página: o campo troca de lugar com um
       formulário de dois campos aqui mesmo. Modal por cima de um
       formulário aberto era a porta antiga. */
    const [criandoCategoria, setCriandoCategoria] = useState(false);
    const [novaCategoria, setNovaCategoria] = useState({ nome: '', descricao: '' });
    const [salvandoCategoria, setSalvandoCategoria] = useState(false);

    const quillRef = useRef<ReactQuill>(null);
    const arquivoRef = useRef<HTMLInputElement>(null);
    /* Onde o cursor estava quando o seletor de arquivo abriu — o clique no
       diálogo do sistema tira o foco do editor e a seleção se perde. */
    const posicaoRef = useRef<number>(0);

    /* ─── Carga ────────────────────────────────────────────────── */

    useEffect(() => {
        let vivo = true;

        (async () => {
            setCarregando(true);
            try {
                const cats = await HelpService.getCategories();
                if (!vivo) return;
                setCategorias(cats);

                if (editando && id) {
                    const art = await HelpService.getArticleById(Number(id));
                    if (!vivo) return;
                    if (!art) {
                        aviso.erro('Não encontrei esse artigo.');
                        navegar('/admin/help');
                        return;
                    }
                    const carregado: Formulario = {
                        titulo: art.titulo,
                        conteudo: art.conteudo,
                        categoriaId: art.categoriaId,
                        tags: art.tags || '',
                    };
                    setArtigo(art);
                    setForm(carregado);
                    setOriginal(carregado);
                } else {
                    const inicial = { ...VAZIO, categoriaId: cats[0]?.id ?? 0 };
                    setForm(inicial);
                    setOriginal(inicial);
                }
            } catch {
                if (!vivo) return;
                aviso.erro('Não foi possível carregar os dados.');
                navegar('/admin/help');
            } finally {
                if (vivo) setCarregando(false);
            }
        })();

        return () => { vivo = false; };
    }, [id, editando, navegar]);

    const alterado = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(original),
        [form, original],
    );

    const campo = <K extends keyof Formulario>(chave: K, valor: Formulario[K]) => {
        setForm(f => ({ ...f, [chave]: valor }));
        setErros(e => (e[chave] ? { ...e, [chave]: undefined } : e));
    };

    /* ─── Imagem ───────────────────────────────────────────────────
       O endpoint `Help/UploadImagem` já existia e estava sem uso: a barra
       do Quill embutia a imagem em base64 dentro do próprio HTML do
       artigo, que é o que faz a leitura baixar centenas de KB. Agora tanto
       o botão quanto o colar passam pelo upload e o que entra no conteúdo
       é uma URL. */

    const subirImagem = useCallback(async (arquivo: File, em: number) => {
        setEnviandoImagem(true);
        try {
            const url = await HelpService.uploadImage(arquivo);
            const editor = quillRef.current?.getEditor();
            if (!editor) return;
            const onde = Math.min(em, editor.getLength());
            editor.insertEmbed(onde, 'image', url, 'user');
            editor.setSelection(onde + 1, 0);
        } catch {
            aviso.erro('Não consegui enviar a imagem.');
        } finally {
            setEnviandoImagem(false);
        }
    }, []);

    const escolherImagem = useCallback(() => {
        const editor = quillRef.current?.getEditor();
        posicaoRef.current = editor?.getSelection(true)?.index ?? editor?.getLength() ?? 0;
        arquivoRef.current?.click();
    }, []);

    const aoEscolherArquivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        if (arquivo) await subirImagem(arquivo, posicaoRef.current);
        if (arquivoRef.current) arquivoRef.current.value = '';
    };

    /* Colar imagem da área de transferência também sobe em vez de virar
       base64 — é por onde o acervo de hoje ganhou os 457 KB de um artigo. */
    useEffect(() => {
        if (carregando) return;
        const editor = quillRef.current?.getEditor();
        const raiz = editor?.root;
        if (!editor || !raiz) return;

        const aoColar = (ev: ClipboardEvent) => {
            const imagem = Array.from(ev.clipboardData?.files ?? [])
                .find(f => f.type.startsWith('image/'));
            if (!imagem) return;
            ev.preventDefault();
            void subirImagem(imagem, editor.getSelection(true)?.index ?? editor.getLength());
        };

        raiz.addEventListener('paste', aoColar);
        return () => raiz.removeEventListener('paste', aoColar);
    }, [carregando, subirImagem]);

    const modulos = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [2, 3, false] }],
                ['bold', 'italic', 'underline'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['blockquote', 'code-block'],
                ['link', 'image'],
                ['clean'],
            ],
            handlers: { image: escolherImagem },
        },
    }), [escolherImagem]);

    /* ─── Salvar ───────────────────────────────────────────────── */

    const salvar = async (comoRascunho: boolean) => {
        const achados: Partial<Record<keyof Formulario, string>> = {};
        if (!form.titulo.trim()) achados.titulo = 'O artigo precisa de um título.';
        if (!form.categoriaId) achados.categoriaId = 'Escolha a categoria onde ele vai aparecer.';
        if (semTexto(form.conteudo)) achados.conteudo = 'O artigo está sem conteúdo.';

        if (Object.keys(achados).length) {
            setErros(achados);
            return;
        }

        setSalvando(true);
        try {
            const dados = { ...form, titulo: form.titulo.trim(), isRascunho: comoRascunho };
            if (editando && id) {
                await HelpService.updateArticle({ ...dados, id: Number(id) });
            } else {
                await HelpService.createArticle(dados);
            }
            aviso.ok(comoRascunho ? 'Rascunho salvo.' : 'Artigo publicado.');
            navegar('/admin/help');
        } catch {
            aviso.erro('Não foi possível salvar o artigo.');
        } finally {
            setSalvando(false);
        }
    };

    const sair = () => {
        if (alterado) setADescartar(true);
        else navegar('/admin/help');
    };

    const criarCategoria = async () => {
        if (!novaCategoria.nome.trim()) return;
        setSalvandoCategoria(true);
        try {
            const criada = await HelpService.createCategory({
                nome: novaCategoria.nome.trim(),
                descricao: novaCategoria.descricao.trim(),
                icone: 'FaBook',
            });
            setCategorias(c => [...c, criada]);
            campo('categoriaId', criada.id);
            setNovaCategoria({ nome: '', descricao: '' });
            setCriandoCategoria(false);
            aviso.ok('Categoria criada.');
        } catch {
            aviso.erro('Não foi possível criar a categoria.');
        } finally {
            setSalvandoCategoria(false);
        }
    };

    /* ─── Montagem ─────────────────────────────────────────────── */

    const eraRascunho = !editando || (artigo?.isRascunho ?? true);
    const titulo = editando ? (artigo?.titulo || '…') : 'Novo artigo';

    const voltar = (
        <button type="button" className="hed-back" onClick={sair}>
            <LuChevronLeft size={14} /> Central de ajuda
        </button>
    );

    const acoes = (
        <div className="hed-tools">
            <button type="button" className="hed-btn" onClick={sair} disabled={salvando}>
                Cancelar
            </button>
            <button type="button" className="hed-btn" onClick={() => salvar(true)} disabled={salvando}>
                {eraRascunho ? 'Salvar rascunho' : 'Voltar para rascunho'}
            </button>
            <button type="button" className="hed-btn is-primary" onClick={() => salvar(false)} disabled={salvando}>
                {salvando ? 'Salvando…' : eraRascunho ? 'Publicar' : 'Salvar alterações'}
            </button>
        </div>
    );

    return (
        <div className="page-shell hed-screen">
            <PageHeader
                leading={voltar}
                title={titulo}
                subtitle="O artigo que os usuários leem na Central de ajuda."
                actions={carregando ? undefined : acoes}
            />

            <div className="page-body hed-body">
                {carregando ? (
                    <div className="hed-col"><p className="hed-sec-sub">Carregando o artigo…</p></div>
                ) : (
                    <div className="hed-ed">
                        <div className="hed-col">
                            <div className="hed-wrap">
                                <section className="hed-sec">
                                    <h2 className="hed-sec-t">
                                        <LuFileText size={15} aria-hidden="true" /> Título
                                        <span className="hed-req">*</span>
                                    </h2>
                                    <p className="hed-sec-sub">
                                        É por ele que a pessoa acha o artigo na busca da ajuda.
                                    </p>
                                    <div className="hed-field">
                                        <input
                                            id="hed-titulo"
                                            type="text"
                                            className={`hed-inp ${erros.titulo ? 'has-err' : ''}`}
                                            value={form.titulo}
                                            onChange={e => campo('titulo', e.target.value)}
                                            placeholder="Ex.: Como criar uma campanha de disparo"
                                        />
                                        {erros.titulo
                                            ? <span className="hed-err"><LuCircleAlert size={12} />{erros.titulo}</span>
                                            : <span className="hed-hint">Uma frase curta, começando pelo que a pessoa quer fazer.</span>}
                                    </div>
                                </section>

                                <section className="hed-sec is-escrita">
                                    <h2 className="hed-sec-t">
                                        <LuPencil size={15} aria-hidden="true" /> Conteúdo
                                        <span className="hed-req">*</span>
                                    </h2>
                                    <p className="hed-sec-sub">
                                        Imagem entra pelo botão da barra ou colando — ela sobe para o
                                        servidor, então o artigo continua leve para quem lê.
                                    </p>

                                    <div className={`hed-quill ${erros.conteudo ? 'has-err' : ''}`}>
                                        <ReactQuill
                                            ref={quillRef}
                                            theme="snow"
                                            value={form.conteudo}
                                            onChange={val => campo('conteudo', val)}
                                            modules={modulos}
                                            placeholder="Escreva o passo a passo aqui…"
                                        />
                                    </div>

                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={arquivoRef}
                                        onChange={aoEscolherArquivo}
                                        style={{ display: 'none' }}
                                    />

                                    {erros.conteudo
                                        ? <span className="hed-err"><LuCircleAlert size={12} />{erros.conteudo}</span>
                                        : enviandoImagem
                                            ? <span className="hed-hint">Enviando a imagem…</span>
                                            : null}
                                </section>
                            </div>
                        </div>

                        <aside className="hed-ficha">
                            <section className="hed-fsec">
                                <h3 className="hed-fsec-t">Publicação</h3>
                                <span className={`hed-st ${eraRascunho ? 'is-off' : 'is-on'}`}>
                                    {eraRascunho ? 'Rascunho' : 'Publicado'}
                                </span>
                                <span className="hed-hint">
                                    {eraRascunho
                                        ? 'Só aparece aqui na gestão. "Publicar" é o que o leva para a ajuda.'
                                        : 'Está visível na Central de ajuda para os usuários.'}
                                </span>
                                {artigo && (
                                    <dl className="hed-kv">
                                        <div><dt>Criado</dt><dd>{dataCurta(artigo.dataCriacao)}</dd></div>
                                        <div><dt>Atualizado</dt><dd>{dataCurta(artigo.dataAtualizacao)}</dd></div>
                                    </dl>
                                )}
                            </section>

                            <section className="hed-fsec">
                                <h3 className="hed-fsec-t">
                                    <LuFolder size={12} aria-hidden="true" /> Categoria
                                </h3>

                                {criandoCategoria ? (
                                    <div className="hed-nova">
                                        <div className="hed-field">
                                            <label className="hed-lab" htmlFor="hed-cat-nome">Nome</label>
                                            <input
                                                id="hed-cat-nome"
                                                type="text"
                                                className="hed-inp"
                                                value={novaCategoria.nome}
                                                onChange={e => setNovaCategoria(c => ({ ...c, nome: e.target.value }))}
                                                placeholder="Ex.: Integrações"
                                                disabled={salvandoCategoria}
                                            />
                                        </div>
                                        <div className="hed-field">
                                            <label className="hed-lab" htmlFor="hed-cat-desc">Descrição</label>
                                            <input
                                                id="hed-cat-desc"
                                                type="text"
                                                className="hed-inp"
                                                value={novaCategoria.descricao}
                                                onChange={e => setNovaCategoria(c => ({ ...c, descricao: e.target.value }))}
                                                placeholder="Opcional"
                                                disabled={salvandoCategoria}
                                            />
                                        </div>
                                        <div className="hed-tools">
                                            <button type="button" className="hed-btn is-sm"
                                                onClick={() => { setCriandoCategoria(false); setNovaCategoria({ nome: '', descricao: '' }); }}
                                                disabled={salvandoCategoria}>
                                                Cancelar
                                            </button>
                                            <button type="button" className="hed-btn is-sm is-primary"
                                                onClick={criarCategoria}
                                                disabled={salvandoCategoria || !novaCategoria.nome.trim()}>
                                                {salvandoCategoria ? 'Criando…' : 'Criar'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="hed-field">
                                            <Select
                                                id="hed-categoria"
                                                size="md"
                                                value={form.categoriaId ? String(form.categoriaId) : ''}
                                                onChange={v => campo('categoriaId', Number(v))}
                                                options={categorias.map(c => ({ value: String(c.id), label: c.nome }))}
                                                placeholder="Escolha a categoria…"
                                                hasError={!!erros.categoriaId}
                                                ariaLabel="Categoria do artigo"
                                            />
                                            {erros.categoriaId && (
                                                <span className="hed-err"><LuCircleAlert size={12} />{erros.categoriaId}</span>
                                            )}
                                        </div>
                                        <button type="button" className="hed-btn is-sm"
                                            onClick={() => setCriandoCategoria(true)}>
                                            <LuPlus size={13} /> Nova categoria
                                        </button>
                                    </>
                                )}
                            </section>

                            <section className="hed-fsec">
                                <h3 className="hed-fsec-t">
                                    <LuTag size={12} aria-hidden="true" /> Tags
                                </h3>
                                <div className="hed-field">
                                    <input
                                        id="hed-tags"
                                        type="text"
                                        className="hed-inp"
                                        value={form.tags}
                                        onChange={e => campo('tags', e.target.value)}
                                        placeholder="campanhas, disparador"
                                    />
                                    <span className="hed-hint">
                                        Separadas por vírgula. A busca da gestão procura por elas.
                                    </span>
                                </div>
                            </section>

                            {/* Histórico (fase 6): criação, título, conteúdo, publicação, exclusão. */}
                            {artigo && (
                                <section className="hed-fsec">
                                    <h3 className="hed-fsec-t">
                                        <LuHistory size={12} aria-hidden="true" /> Histórico
                                    </h3>
                                    <SecaoHistorico entidade="artigo_ajuda" id={artigo.id} variante="pagina" />
                                </section>
                            )}
                        </aside>
                    </div>
                )}
            </div>

            <Dialog
                open={aDescartar}
                onClose={() => setADescartar(false)}
                title={`Descartar as alterações em «${form.titulo || 'artigo sem título'}»?`}
                confirmLabel="Descartar"
                cancelLabel="Continuar editando"
                danger
                onConfirm={() => { setADescartar(false); navegar('/admin/help'); }}
            >
                O que foi escrito desde a última vez que você salvou se perde.
            </Dialog>
        </div>
    );
};

export default HelpArticleEditorPage;
